import { useState, useRef, useEffect } from "react";
import { useSingleton } from "../../hooks/use-singleton";
import { useApi } from "../../hooks/use-api";
import {
  API_COMPETITIVE_EXAM,
  API_COMPETITIVE_YEAR,
  API_COMPETITIVE_MEDIUIM,
  API_COMPETITIVE_PAPER,
  API_PYQ_COMPETITIVE,
  API_PYQ_COMPETITIVE_EVALUATE,
  API_COMPETITIVE_EXAM_BANKING,
  API_COMPETITIVE_YEAR_BANKING,
  API_COMPETITIVE_MEDIUIM_BANKING,
  API_COMPETITIVE_PAPER_BANKING,
  API_PYQ_BANKING,
  API_PYQ_BANKING_EVALUATE,
} from "../../utils/api-routes";

const useHandler = () => {
  const { user, mail } = useSingleton();
  const { post } = useApi();

  // ── Filter State ────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState("BANKING");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [selectedPaper, setSelectedPaper] = useState("");

  // ── Dropdown Data ────────────────────────────────────────────────
  const [exams, setExams] = useState([]);
  const [years, setYears] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [papers, setPapers] = useState([]);

  // ── Quiz / UI State ──────────────────────────────────────────────
  const [openDropdown, setOpenDropdown] = useState(null);
  const [questionDetails, setQuestionDetails] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [markDetails, setMarkDetails] = useState({});
  const [showMarks, setShowMarks] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mode, setMode] = useState(false);
  const [hidePanals, setHidePanals] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showSetupPopup, setShowSetupPopup] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────
  const examRef = useRef(null);
  const yearRef = useRef(null);
  const mediumRef = useRef(null);
  const paperRef = useRef(null);
  const selectedAnswersRef = useRef(selectedAnswers);

  // ── Enable start only when all 4 filters chosen ──────────────────
  const isStartEnabled = !!selectedExam && !!selectedYear && !!selectedPaper;

  // ── 1. Load exams on mount ────────────────────────────────────────
  useEffect(() => {
    const fetchExams = async () => {
      setIsLoading(true);
      try {
        const res = await post(API_COMPETITIVE_EXAM_BANKING, {});
        if (res) setExams(res?.data?.exams || []);
      } catch (e) {
        console.error("Error loading exams", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExams();
  }, []);

  // ── 2. Load years when exam changes ──────────────────────────────
  useEffect(() => {
    if (!selectedExam) {
      setYears([]);
      setSelectedYear("");
      return;
    }
    const fetchYears = async () => {
      setIsLoading(true);
      setSelectedYear("");
      setSelectedMedium("");
      setSelectedPaper("");
      try {
        const res = await post(API_COMPETITIVE_YEAR_BANKING, {
          exam: selectedExam,
        });
        if (res) setYears(res?.data?.year || []);
      } catch (e) {
        console.error("Error loading years", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchYears();
  }, [selectedExam]);

  // ── 3. Load mediums when exam + year are set ────────────────────────────
  useEffect(() => {
    if (!selectedExam || !selectedYear) {
      setMediums([]);
      setSelectedMedium("");
      return;
    }
    const fetchMediums = async () => {
      setIsLoading(true);
      setSelectedMedium("");
      setSelectedPaper("");
      try {
        const res = await post(API_COMPETITIVE_MEDIUIM, {
          exam: selectedExam,
          year: selectedYear,
        });
        if (res) setMediums(res?.data?.medium || []);
      } catch (e) {
        console.error("Error loading mediums", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMediums();
  }, [selectedExam, selectedYear]);

  // ── 4. Load papers when exam + year + medium are all set ──────────
  useEffect(() => {
    if (!selectedExam || !selectedYear) {
      setPapers([]);
      setSelectedPaper("");
      return;
    }
    const fetchPapers = async () => {
      setIsLoading(true);
      setSelectedPaper("");
      try {
        const res = await post(API_COMPETITIVE_PAPER_BANKING, {
          exam: selectedExam,
          year: selectedYear,
          medium: (user?.data?.medium || "").toUpperCase(),
        });
        if (res) setPapers(res?.data?.paper || []);
      } catch (e) {
        console.error("Error loading papers", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPapers();
  }, [selectedExam, selectedYear, user]);

  // ── Close dropdowns on outside click ─────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        examRef.current?.contains(e.target) ||
        yearRef.current?.contains(e.target) ||
        mediumRef.current?.contains(e.target) ||
        paperRef.current?.contains(e.target)
      )
        return;
      setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    selectedAnswersRef.current = selectedAnswers;
  }, [selectedAnswers]);

  // ── Fetch MCQ questions ───────────────────────────────────────────
  const getMcqdetails = async () => {
    try {
      const payload = {
        mail,
        exam: selectedExam,
        year: selectedYear,
        medium: user?.data?.medium,
        paper: selectedPaper,
      };
      const res = await post(API_PYQ_BANKING, payload);
      if (res) {
        setQuestionDetails(res?.data);
      }
    } catch (e) {
      console.error("Error fetching MCQ", e);
      const status = e?.response?.status;

      if (status === 403) {
        setHidePanals(false);
        setIsQuizActive(false);
        setQuestionDetails([]);
      }
    }
  };

  // ── Normalize questions ───────────────────────────────────────────
  const normalizeQuestions = (apiData = []) =>
    apiData.map((q) => ({
      question: q["QUESTION"],
      options: [q["OPTION A"], q["OPTION B"], q["OPTION C"], q["OPTION D"]],
      answer: q["ANSWER"],
      questionId: q["QUESTION_ID"],
    }));

  useEffect(() => {
    if (questionDetails?.length) {
      setQuestions(normalizeQuestions(questionDetails));
    }
  }, [questionDetails]);

  // ── Quiz Duration ─────────────────────────────────────────────────
  const getQuizDuration = () => 120 * 60;

  // ── Navigation ───────────────────────────────────────────────────
  const currentQuestion = questions[currentQIndex];
  const selectedOption = selectedAnswers[currentQIndex];
  const isAnswered = selectedOption !== undefined;
  const isCorrect =
    isAnswered &&
    currentQuestion?.options[selectedOption] === currentQuestion?.answer;

  const handleOptionSelect = (index, qIndex) => {
    const targetIdx = qIndex !== undefined ? qIndex : currentQIndex;
    if (!mode && selectedAnswers[targetIdx] !== undefined) return;
    setSelectedAnswers((prev) => ({ ...prev, [targetIdx]: index }));
  };

  const handleNext = () => {
    if (currentQIndex < questions.length - 1) setCurrentQIndex((p) => p + 1);
  };
  const handlePrevious = () => {
    if (currentQIndex > 0) setCurrentQIndex((p) => p - 1);
  };

  // ── Submit ────────────────────────────────────────────────────────
  // const buildSubmitPayload = () =>
  //   questions.map((q, index) => ({
  //     PAPER: selectedPaper,
  //     QUESTION: q.question,
  //     ANSWER:
  //       selectedAnswers[index] !== undefined
  //         ? q.options[selectedAnswers[index]]
  //         : "",
  //     QUESTION_ID: q.questionId || "",
  //     YEAR: selectedYear,
  //   }));

  const buildSubmitPayload = () =>
    questions.map((q, index) => {
      const selectedIndex = selectedAnswersRef.current[index]; // ✅ latest

      return {
        QUESTION: q.question,
        ANSWER: selectedIndex !== undefined ? q.options[selectedIndex] : "",
        QUESTION_ID: q.questionId || "",
        YEAR: selectedYear,
        PAPER: selectedPaper,
      };
    });

  const onSubmitQuiz = async () => {
    setShowMarks(true);
    setSubmitLoading(true);
    setIsReviewMode(false);
    setShowReviewPopup(false);
    setIsQuizActive(false);
    setHidePanals(true);
    try {
      const res = await post(API_PYQ_BANKING_EVALUATE, {
        mail,
        exam: selectedExam,
        year: selectedYear,
        medium: (user?.data?.medium || "").toUpperCase(),
        paper: selectedPaper,
        answers: buildSubmitPayload(),
      });
      if (res) {
        setMarkDetails(res?.data);
        setPanelOpen(false);
        setSubmitLoading(false);
        setHidePanals(true);
      }
    } catch (e) {
      setSubmitLoading(false);
      console.error("Error submitting quiz", e);
    }
  };

  const confirmStartQuiz = () => {
    getMcqdetails();
    setShowSetupPopup(false);
  };

  const onStartQuiz = () => setShowSetupPopup(true);

  // ── Reset / Cancel ────────────────────────────────────────────────
  const onCancel = () => {
    setIsQuizActive(false);
    setHidePanals(true);
    setShowMarks(false);
    setMarkDetails({});
    setQuestionDetails([]);
    setQuestions([]);
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setSelectedPaper("");
    setSelectedYear("");
    setHidePanals(false);
    setSubmitLoading(false);
    setTimeRemaining(0);
    setShowReviewPopup(false);
    setIsReviewMode(false);
  };

  const resetQuizState = () => {
    setIsQuizActive(true);
    setShowMarks(false);
    setMarkDetails({});
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setHidePanals(true);
    setSubmitLoading(false);
    setShowReviewPopup(false);
    setIsReviewMode(false);
  };

  const onRetakeQuiz = async () => {
    resetQuizState();
    if (questionDetails?.length) {
      setQuestions(normalizeQuestions(questionDetails));
    }
  };

  const onToggleQuizBtn = () => {
    setMode((p) => !p);
    // setSelectedYear("");
    // setSelectedMedium("");
    // setSelectedPaper("");
  };

  const getQuestionStatus = (index) => {
    if (index === currentQIndex) return "current";

    if (selectedAnswers[index] !== undefined) {
      return "answered";
    }

    if (index < currentQIndex) {
      return "not-answered";
    }

    return "not-visited";
  };

  return {
    // filter state
    mail,
    selectedExam,
    setSelectedExam,
    selectedYear,
    setSelectedYear,
    selectedMedium,
    setSelectedMedium,
    selectedPaper,
    setSelectedPaper,
    // dropdown data
    exams,
    years,
    mediums,
    papers,
    // refs
    examRef,
    yearRef,
    mediumRef,
    paperRef,
    // ui
    openDropdown,
    setOpenDropdown,
    isStartEnabled,
    isLoading,
    panelOpen,
    setPanelOpen,
    // quiz
    questions,
    currentQuestion,
    currentQIndex,
    questionDetails,
    selectedOption,
    isAnswered,
    isCorrect,
    selectedAnswers,
    markDetails,
    showMarks,
    isQuizActive,
    user,
    mode,
    hidePanals,
    submitLoading,
    timeRemaining,
    setTimeRemaining,
    showSetupPopup,
    setShowSetupPopup,
    showReviewPopup,
    setShowReviewPopup,
    isReviewMode,
    setIsReviewMode,
    setCurrentQIndex,
    setHidePanals,
    setIsQuizActive,
    getQuizDuration,
    handleOptionSelect,
    handleNext,
    handlePrevious,
    onStartQuiz,
    confirmStartQuiz,
    onSubmitQuiz,
    onCancel,
    onRetakeQuiz,
    getQuestionStatus,
    onToggleQuizBtn,
  };
};

export default useHandler;
